/**
 * 작성자 : 한영광
 * 작성일자 : 2024.09.04
 * 기능 : 다국어 TTS 오디오 파일 생성
 */
import Crunker from 'crunker';
import { TtsLangType, TtsPauseLevel, TtsVoiceDefinition, getDefaultVoiceOption, lanCdToTtsType } from '../constants/ttsVoices';
import { externalApiPost } from '../services/_common';

const PAUSE_MS: Record<TtsPauseLevel, { space: number; punct: number }> = {
  none: { space: 0, punct: 0 },
  short: { space: 300, punct: 200 },
  medium: { space: 500, punct: 400 },
  long: { space: 800, punct: 600 },
};

const escapeXml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const buildTtsSsml = (text: string, pauseLevel: TtsPauseLevel): string => {
  const trimmed = text.trim();
  if (pauseLevel === 'none') {
    return `<speak>${escapeXml(trimmed)}</speak>`;
  }

  const { space, punct } = PAUSE_MS[pauseLevel];
  let processed = escapeXml(trimmed);
  processed = processed.replace(/ {2,}/g, ` <break time="${space}ms"/> `);
  if (punct > 0) {
    processed = processed.replace(/([,.!?。，、])\s*/g, `$1<break time="${punct}ms"/>`);
  }
  return `<speak>${processed}</speak>`;
};

function base64ToBlob(base64String: string) {
  // Base64를 바이너리로 변환
  const byteString = atob(base64String);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i += 1) {
    uint8Array[i] = byteString.charCodeAt(i);
  }

  // Blob을 생성
  return new Blob([uint8Array], { type: 'audio/mp3' });
}

export type TtsSynthesizeOptions = {
  text: string;
  type: TtsLangType;
  speakingRate?: number;
  pauseLevel?: TtsPauseLevel;
  voice?: Pick<TtsVoiceDefinition, 'voiceId' | 'languageCode' | 'ssmlGender'>;
};

export const synthesizeTts = async ({ text, type, speakingRate = 1, pauseLevel = 'none', voice }: TtsSynthesizeOptions): Promise<Blob> => {
  const resolvedVoice = voice ?? getDefaultVoiceOption(type);
  const TTSURL = process.env.REACT_APP_GOOGLE_CLOUD_TTS_URL || '';
  const useSsml = pauseLevel !== 'none';
  const payload = {
    audioConfig: {
      audioEncoding: 'mp3',
      speakingRate,
      sampleRateHertz: 44100,
    },
    input: useSsml ? { ssml: buildTtsSsml(text, pauseLevel) } : { text },
    voice: {
      languageCode: resolvedVoice.languageCode,
      name: resolvedVoice.voiceId,
      ssmlGender: resolvedVoice.ssmlGender,
    },
  };

  const response = await externalApiPost(TTSURL, payload);
  return base64ToBlob(response.data.audioContent);
};

export type TtsDownloadOptions = TtsSynthesizeOptions & {
  fileName?: string;
};

type LegacyTtsTypes = {
  text: string;
  type: TtsLangType;
  fileName?: string;
  speakingRate?: number;
  pauseLevel?: TtsPauseLevel;
  voice?: Pick<TtsVoiceDefinition, 'voiceId' | 'languageCode' | 'ssmlGender'>;
};

const tts = async ({ text, type, fileName, speakingRate, pauseLevel, voice }: LegacyTtsTypes) => {
  const blob = await synthesizeTts({ text, type, speakingRate, pauseLevel, voice });
  if (fileName) {
    const crunker = new Crunker();
    crunker.download(blob, fileName);
  }
};

export { lanCdToTtsType };
export default tts;
